/**
 * The browser wrapper around the pure save format in @zookeepbit/sim. A broken or newer save is never destroyed: a corrupt
 * one is kept as a backup key and the game starts fresh; one from a newer version of the game is left untouched (the
 * session plays in memory). If localStorage is unavailable or full, the game keeps running in memory.
 */
import { deserialize, emptySave, serialize, type SaveGame } from "@zookeepbit/sim";
import type { SaveStore } from "./game";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** ok: loaded a save. fresh: nothing saved yet. recovered: a broken save was backed up. readonly: newer save, not overwritten. memory: no usable storage. */
export type StoreStatus = "ok" | "fresh" | "recovered" | "readonly" | "memory";

export function createLocalStore(storage: StorageLike | null, key = "zookeepbit.save"): SaveStore & { status(): StoreStatus } {
  let status: StoreStatus = "memory";
  let current: SaveGame = emptySave();

  if (storage) {
    try {
      const text = storage.getItem(key);
      if (text === null) {
        status = "fresh";
      } else {
        const result = deserialize(text);
        if (result.ok) {
          current = result.data;
          status = "ok";
        } else if (result.error.kind === "future-version") {
          status = "readonly";
        } else {
          try {
            storage.setItem(`${key}.backup-${Date.now()}`, text);
          } catch {
            // No room for a backup: the game can still start fresh and save over the broken save.
          }
          status = "recovered";
        }
      }
    } catch {
      status = "memory";
    }
  }

  return {
    status: () => status,
    load: () => current,
    save(game) {
      current = game;
      if (!storage || status === "readonly" || status === "memory") return;
      try {
        storage.setItem(key, serialize(game));
        if (status === "fresh" || status === "recovered") status = "ok";
      } catch {
        status = "memory";
      }
    },
  };
}
